import React, { useState, useEffect } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "../../Styles/PC/PCConfig.css";

import {
    getAllTestsForGeneticProgramAPI,
    createProgramAPI,
    getAllWellnessProgramsAPI
} from "../../services/pcConfigService";
import { toast } from 'react-toastify';

/* ---------------- CONSTANTS ---------------- */

const PHASE_NAMES = ["Repair", "Maintain", "Enhance", "Sustain"];

const getDefaultPhases = () =>
    PHASE_NAMES.map((name, index) => ({
        id: index + 1,
        name,
        selectedTests: [],
        reviewTests: [],
        consultationLimits: {
            generalPhysician: 1,
            dietician: 2,
            fitnessCounsellor: 2,
            specialist: 1
        },
        reviewConsultation: { gp: 1 }
    }));

const CONSULTATION_TYPES = [
    { key: "generalPhysician", label: "GP" },
    { key: "dietician", label: "Diet" },
    { key: "fitnessCounsellor", label: "Fitness" },
    { key: "specialist", label: "Specialist" }
];

const ProgrammeConfig = () => {
    const [phases, setPhases] = useState([]);
    const [expanded, setExpanded] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [loading, setLoading] = useState(true);

    const [programMeta, setProgramMeta] = useState({
        programName: "",
        description: "",
        _id: ''
    });

    const [searchQueries, setSearchQueries] = useState({});
    const [searchResults, setSearchResults] = useState({});

    const [reviewSearchQueries, setReviewSearchQueries] = useState({});
    const [reviewSearchResults, setReviewSearchResults] = useState({});

    /* ================= FETCH PROGRAM ================= */

    useEffect(() => {
        fetchPrograms();
    }, []);

    const fetchPrograms = async () => {
        try {
            const response = await getAllWellnessProgramsAPI();
            const programs = response?.programs || [];

            // 🔹 IF EMPTY → CREATE MODE
            if (programs.length === 0) {
                setProgramMeta({
                    _id: '',
                    programName: "Advanced Longevity Program",
                    description:
                        "Advanced genetic wellness program with personalized diagnostics"
                });

                setPhases(getDefaultPhases());
                setLoading(false);
                return;
            }

            // 🔹 EDIT MODE
            const program = programs[0];

            setProgramMeta({
                programName: program.programName,
                description: program.description,
                _id: program._id
            });

            const mappedPhases = program.phases.map((phase) => ({
                id: phase.phaseNumber,
                name: phase.phaseName,
                selectedTests: phase.diagnostics.map((test) => ({
                    id: test.testID,
                    name: test.testName,
                    code: test.shortCode || "-",
                    category: test.category,
                    department: test.department || "",
                    diagnosticType: test.category || "",
                    isPanel: test.isPanel || false
                })),
                reviewTests: (phase.reviewDiagnostics || []).map((test) => ({
                    id: test.testID,
                    name: test.testName,
                    code: test.shortCode || "-",
                    category: test.category,
                    department: test.department || "",
                    diagnosticType: test.category || "",
                    isPanel: test.isPanel || false
                })),
                consultationLimits: {
                    generalPhysician: phase.maxConsultations?.gp || 0,
                    dietician: phase.maxConsultations?.dietician || 0,
                    fitnessCounsellor: phase.maxConsultations?.fitness || 0,
                    specialist: phase.maxConsultations?.specialist || 0
                },
                reviewConsultation: {
                    gp: phase.reviewConsultation?.gp ?? 1
                }
            }));

            setPhases(mappedPhases);
        } catch (error) {
            console.error("Fetch error:", error);
            setPhases(getDefaultPhases());
        } finally {
            setLoading(false);
        }
    };

    /* ================= SEARCH ================= */

    const handleSearchChange = async (phaseId, value) => {
        setSearchQueries((prev) => ({
            ...prev,
            [phaseId]: value
        }));

        if (value.length < 2) {
            setSearchResults((prev) => ({ ...prev, [phaseId]: [] }));
            return;
        }

        try {
            const response = await getAllTestsForGeneticProgramAPI(value);

            const combined = [
                ...(response?.tests || []),
                ...(response?.geneticTests || []),
                ...(response?.radiologyTests || [])
            ];

            const formatted = combined.map((test) => ({
                id: test.testID,
                name: test.testName,
                code: test.shortCode || "-",
                category: test.category,
                department: test.department || "",
                diagnosticType: test.category || "",
                isPanel: test.isPanel || false
            }));

            setSearchResults((prev) => ({
                ...prev,
                [phaseId]: formatted
            }));
        } catch (error) {
            console.error("Search API error:", error);
        }
    };

    /* ================= ADD TEST ================= */

    const handleAddTest = (phaseId, test) => {
        setPhases((prev) =>
            prev.map((phase) => {
                if (phase.id !== phaseId) return phase;

                const exists = phase.selectedTests.some(
                    (t) => t.id === test.id
                );
                if (exists) return phase;

                return {
                    ...phase,
                    selectedTests: [...phase.selectedTests, test]
                };
            })
        );

        setSearchQueries((prev) => ({ ...prev, [phaseId]: "" }));
        setSearchResults((prev) => ({ ...prev, [phaseId]: [] }));
        setHasChanges(true);
    };

    const handleRemoveTest = (phaseId, testId) => {
        setPhases((prev) =>
            prev.map((phase) =>
                phase.id === phaseId
                    ? {
                        ...phase,
                        selectedTests: phase.selectedTests.filter(
                            (t) => t.id !== testId
                        )
                    }
                    : phase
            )
        );
        setHasChanges(true);
    };

    /* ================= REVIEW TEST SEARCH / ADD / REMOVE ================= */

    const handleReviewSearchChange = async (phaseId, value) => {
        setReviewSearchQueries((prev) => ({ ...prev, [phaseId]: value }));

        if (value.length < 2) {
            setReviewSearchResults((prev) => ({ ...prev, [phaseId]: [] }));
            return;
        }

        try {
            const response = await getAllTestsForGeneticProgramAPI(value);
            const combined = [
                ...(response?.tests || []),
                ...(response?.geneticTests || []),
                ...(response?.radiologyTests || [])
            ];
            const formatted = combined.map((test) => ({
                id: test.testID,
                name: test.testName,
                code: test.shortCode || "-",
                category: test.category,
                department: test.department || "",
                diagnosticType: test.category || "",
                isPanel: test.isPanel || false
            }));
            setReviewSearchResults((prev) => ({ ...prev, [phaseId]: formatted }));
        } catch (error) {
            console.error("Review search API error:", error);
        }
    };

    const handleAddReviewTest = (phaseId, test) => {
        setPhases((prev) =>
            prev.map((phase) => {
                if (phase.id !== phaseId) return phase;
                if (phase.reviewTests.some((t) => t.id === test.id)) return phase;
                return { ...phase, reviewTests: [...phase.reviewTests, test] };
            })
        );
        setReviewSearchQueries((prev) => ({ ...prev, [phaseId]: "" }));
        setReviewSearchResults((prev) => ({ ...prev, [phaseId]: [] }));
        setHasChanges(true);
    };

    const handleRemoveReviewTest = (phaseId, testId) => {
        setPhases((prev) =>
            prev.map((phase) =>
                phase.id === phaseId
                    ? { ...phase, reviewTests: phase.reviewTests.filter((t) => t.id !== testId) }
                    : phase
            )
        );
        setHasChanges(true);
    };

    /* ================= REVIEW GP CONSULTATION ================= */

    const handleReviewGpChange = (phaseId, value) => {
        const num = parseInt(value) || 0;
        setPhases((prev) =>
            prev.map((phase) =>
                phase.id === phaseId
                    ? { ...phase, reviewConsultation: { ...phase.reviewConsultation, gp: Math.max(0, num) } }
                    : phase
            )
        );
        setHasChanges(true);
    };

    /* ================= CONSULT LIMIT ================= */

    const handleLimitChange = (phaseId, key, value) => {
        const num = parseInt(value) || 0;

        setPhases((prev) =>
            prev.map((phase) =>
                phase.id === phaseId
                    ? {
                        ...phase,
                        consultationLimits: {
                            ...phase.consultationLimits,
                            [key]: Math.max(0, Math.min(99, num))
                        }
                    }
                    : phase
            )
        );

        setHasChanges(true);
    };

    const getTotalConsultations = (limits) =>
        limits.generalPhysician +
        limits.dietician +
        limits.fitnessCounsellor +
        limits.specialist;

    /* ================= SAVE ================= */

    const handleSave = async () => {
        const requestObj = {
            programName: programMeta.programName,
            description: programMeta.description,
            _id: programMeta._id,
            totalPhases: phases.length,
            phases: phases.map((phase) => ({
                phaseNumber: phase.id,
                phaseName: phase.name,
                isActive: true,
                diagnostics: phase.selectedTests.map((test) => ({
                    diagnosticType: test.category,
                    testID: test.id,
                    testName: test.name,
                    shortCode: test.code,
                    category: test.category,
                    department: test.department,
                    isPanel: test.isPanel
                })),
                reviewDiagnostics: phase.reviewTests.map((test) => ({
                    diagnosticType: test.category,
                    testID: test.id,
                    testName: test.name,
                    shortCode: test.code,
                    category: test.category,
                    department: test.department,
                    isPanel: test.isPanel
                })),
                maxConsultations: {
                    gp: phase.consultationLimits.generalPhysician,
                    dietician: phase.consultationLimits.dietician,
                    fitness: phase.consultationLimits.fitnessCounsellor,
                    specialist: phase.consultationLimits.specialist
                },
                reviewConsultation: {
                    gp: phase.reviewConsultation.gp
                }
            }))
        };

        const response = await createProgramAPI(requestObj);
        setHasChanges(false);
        if (response.status == 'success') {
            toast.success(response.message)
            fetchPrograms()
        } else {
            toast.error(response.message)
        }
    };

    /* ================= RENDER ================= */

    if (loading) {
        return <div className="config-container">Loading...</div>;
    }

    return (
        <div className="config-container">
            <div className="config-header">
                <div>
                    <h2>{programMeta.programName}</h2>
                    <p>{programMeta.description}</p>
                </div>

                <button
                    className={`save-btn ${!hasChanges ? "disabled" : ""}`}
                    disabled={!hasChanges}
                    onClick={handleSave}
                >
                    Save Changes
                </button>
            </div>

            {phases.map((phase) => {
                const query = searchQueries[phase.id] || "";
                const results = searchResults[phase.id] || [];
                const reviewQuery = reviewSearchQueries[phase.id] || "";
                const reviewResults = reviewSearchResults[phase.id] || [];
                const totalConsultations = getTotalConsultations(
                    phase.consultationLimits
                );

                return (
                    <Accordion
                        key={phase.id}
                        expanded={expanded === phase.id}
                        onChange={() =>
                            setExpanded(
                                expanded === phase.id ? null : phase.id
                            )
                        }
                        className="phase-accordion"
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <div className="pc-config-phase-header">
                                <div className="phase-circle">
                                    {phase.id}
                                </div>
                                <div>
                                    <div className="phase-title">
                                        Phase {phase.id}: {phase.name}
                                    </div>
                                    {/* <div className="phase-sub">
                                        {phase.selectedTests.length} tests •{" "}
                                        {totalConsultations} consultations
                                    </div> */}
                                </div>
                            </div>
                        </AccordionSummary>

                        <AccordionDetails>
                            <div className="phase-body">
                                {/* Diagnostic Tests */}
                                <div className="card">
                                    <h4>Diagnostic Tests</h4>
                                    <p className="sub-text">
                                        Search and add tests for this phase
                                    </p>

                                    <input
                                        type="text"
                                        className="search-input"
                                        placeholder="Search tests..."
                                        value={query}
                                        onChange={(e) =>
                                            handleSearchChange(
                                                phase.id,
                                                e.target.value
                                            )
                                        }
                                    />

                                    {query.length > 1 && (
                                        <div className="search-dropdown">
                                            {results.length > 0 ? (
                                                results
                                                    .slice(0, 8)
                                                    .map((test) => (
                                                        <div
                                                            key={test.id}
                                                            className="dropdown-item"
                                                            onClick={() =>
                                                                handleAddTest(
                                                                    phase.id,
                                                                    test
                                                                )
                                                            }
                                                        >
                                                            <div className="dropdown-name">
                                                                {test.name}
                                                            </div>
                                                            <div className="dropdown-meta">
                                                                <Chip
                                                                    label={
                                                                        test.category
                                                                    }
                                                                    size="small"
                                                                    sx={{
                                                                        mr: 1
                                                                    }}
                                                                />
                                                                {test.code} •{" "}
                                                                {
                                                                    test.department
                                                                }
                                                            </div>
                                                        </div>
                                                    ))
                                            ) : (
                                                <div className="no-results">
                                                    No tests found
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="selected-tests">
                                        {phase.selectedTests.map((test) => (
                                            <div
                                                key={test.id}
                                                className="test-card"
                                            >
                                                <div className="test-card-content">
                                                    <div className="test-card-name">
                                                        {test.name}
                                                    </div>
                                                    <div className="test-card-meta">
                                                        <span className="category-badge">
                                                            {test.category}
                                                        </span>
                                                        {test.code} •{" "}
                                                        {test.department}
                                                    </div>
                                                </div>

                                                <button
                                                    className="remove-icon"
                                                    onClick={() =>
                                                        handleRemoveTest(
                                                            phase.id,
                                                            test.id
                                                        )
                                                    }
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Consultation Limits */}
                                <div className="card">
                                    <h4>Consultation Limits</h4>
                                    <p className="sub-text">
                                        Max sessions available in this phase
                                    </p>

                                    <div className="consult-grid">
                                        {CONSULTATION_TYPES.map((c) => (
                                            <div
                                                key={c.key}
                                                className="consult-item"
                                            >
                                                <label>{c.label}</label>
                                                <input
                                                    type="number"
                                                    value={
                                                        phase
                                                            .consultationLimits[
                                                        c.key
                                                        ]
                                                    }
                                                    // disabled={c.key === "generalPhysician"}
                                                    onChange={(e) =>
                                                        handleLimitChange(
                                                            phase.id,
                                                            c.key,
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Review Test Selection */}
                                <div className="card">
                                    <h4>Review Test Selection</h4>
                                    <p className="sub-text">
                                        Search and add review tests for this phase
                                    </p>

                                    <input
                                        type="text"
                                        className="search-input"
                                        placeholder="Search review tests..."
                                        value={reviewQuery}
                                        onChange={(e) =>
                                            handleReviewSearchChange(
                                                phase.id,
                                                e.target.value
                                            )
                                        }
                                    />

                                    {reviewQuery.length > 1 && (
                                        <div className="search-dropdown">
                                            {reviewResults.length > 0 ? (
                                                reviewResults
                                                    .slice(0, 8)
                                                    .map((test) => (
                                                        <div
                                                            key={test.id}
                                                            className="dropdown-item"
                                                            onClick={() =>
                                                                handleAddReviewTest(
                                                                    phase.id,
                                                                    test
                                                                )
                                                            }
                                                        >
                                                            <div className="dropdown-name">
                                                                {test.name}
                                                            </div>
                                                            <div className="dropdown-meta">
                                                                <Chip
                                                                    label={test.category}
                                                                    size="small"
                                                                    sx={{ mr: 1 }}
                                                                />
                                                                {test.code} •{" "}
                                                                {test.department}
                                                            </div>
                                                        </div>
                                                    ))
                                            ) : (
                                                <div className="no-results">
                                                    No tests found
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="selected-tests">
                                        {phase.reviewTests.map((test) => (
                                            <div
                                                key={test.id}
                                                className="test-card"
                                            >
                                                <div className="test-card-content">
                                                    <div className="test-card-name">
                                                        {test.name}
                                                    </div>
                                                    <div className="test-card-meta">
                                                        <span className="category-badge">
                                                            {test.category}
                                                        </span>
                                                        {test.code} •{" "}
                                                        {test.department}
                                                    </div>
                                                </div>

                                                <button
                                                    className="remove-icon"
                                                    onClick={() =>
                                                        handleRemoveReviewTest(
                                                            phase.id,
                                                            test.id
                                                        )
                                                    }
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Review GP Consultation */}
                                <div className="card">
                                    <h4>Review GP Consultation</h4>
                                    <p className="sub-text">
                                        Number of GP consultations for review
                                    </p>

                                    <div className="consult-grid">
                                        <div className="consult-item">
                                            <label>GP Consultations</label>
                                            <input
                                                type="number"
                                                min="0"
                                                value={phase.reviewConsultation.gp}
                                                onChange={(e) =>
                                                    handleReviewGpChange(
                                                        phase.id,
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </div>
    );
};

export default ProgrammeConfig;